/**
 * /terms (OCP-525) - the public page with the governing Terms text, served
 * from the same immutable registry row every acceptance references. No
 * session required: a person must be able to read what they are about to
 * accept, and a customer must be able to re-read what they accepted.
 *
 * OCP-847 - `?kind=avv` serves the data processing agreement (Art. 28 GDPR);
 * `?version=<label>` serves exactly that version (what an order confirmation
 * or a change notice links to), `?lang=` the language variant.
 */
import { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';

import { OrbotoLockup } from '../components/OrbotoLockup';
import { api, ApiError } from '../lib/api';
import { LOCALE_LABELS, currentLocale } from '../i18n';
import { LegalFooter } from '../components/LegalFooter';

export interface TermsVersionSummary {
  id: string;
  /** OCP-847 */
  kind?: 'agb' | 'avv';
  version: string;
  title: string;
  language: string;
  content_sha256: string;
  effective_from: string;
  objection_deadline: string | null;
  published_at: string;
  current: boolean;
}

interface TermsCurrentResponse {
  version: TermsVersionSummary & { content_md: string };
  /** OCP-657 */
  available_languages?: string[];
  governing_language?: string;
}

export function TermsPage() {
  const { t } = useTranslation('terms');
  const [params] = useSearchParams();
  const kind = params.get('kind') === 'avv' ? 'avv' : 'agb';
  const pinnedVersion = params.get('version');
  const [data, setData] = useState<TermsCurrentResponse['version'] | null>(null);
  const [languages, setLanguages] = useState<string[]>([]);
  const [lang, setLang] = useState<string>(params.get('lang') ?? currentLocale());
  const [state, setState] = useState<'loading' | 'ok' | 'unpublished' | 'notfound' | 'error'>('loading');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // OCP-657 - the text in the reader's language; German governs.
        // OCP-847 - one document kind; a pinned version or the current one.
        const q = new URLSearchParams({ lang, ...(kind === 'avv' ? { kind } : {}), ...(pinnedVersion ? { version: pinnedVersion } : {}) });
        const r = await api<TermsCurrentResponse>(`/v1/portal/terms/${pinnedVersion ? 'version' : 'current'}?${q.toString()}`);
        if (cancelled) return;
        setData(r.version);
        setLanguages(r.available_languages ?? [r.version.language]);
        setState('ok');
      } catch (err) {
        if (cancelled) return;
        setState(err instanceof ApiError && err.status === 404 ? (pinnedVersion ? 'notfound' : 'unpublished') : 'error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [lang, kind, pinnedVersion]);

  return (
    <div className="min-h-dvh bg-gray-50 dark:bg-gray-800 px-4 py-10 text-gray-900 dark:text-gray-100">
      <div className="mx-auto w-full max-w-3xl rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-8 shadow-sm">
        <div className="mb-6 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2">
            <OrbotoLockup className="h-6 w-auto" />
          </Link>
          {data && (
            <span className="text-xs text-gray-500" data-testid="terms-version-badge">
              {t('versionBadge', { version: data.version, date: data.effective_from.slice(0, 10) })}
            </span>
          )}
        </div>

        {state === 'loading' && <p className="text-sm text-gray-500">{t('loading')}</p>}
        {state === 'unpublished' && (
          <p className="text-sm text-gray-600 dark:text-gray-400" data-testid="terms-unpublished">
            {kind === 'avv' ? t('unpublishedAvv') : t('unpublished')}
          </p>
        )}
        {state === 'notfound' && (
          <p className="text-sm text-gray-600 dark:text-gray-400" data-testid="terms-notfound">
            {t('notFound')}
          </p>
        )}
        {state === 'error' && (
          <p role="alert" className="text-sm text-red-700 dark:text-red-300">
            {t('error')}
          </p>
        )}
        {state === 'ok' && data && (
          <>
            {data.language !== 'de' && languages.includes('de') && (
              <p className="mb-3 rounded border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 px-3 py-2 text-xs text-amber-900 dark:text-amber-200" data-testid="terms-translation-note">
                {t('translationNote')}
              </p>
            )}
            {languages.length > 1 && (
              <p className="mb-3 text-xs text-gray-500" data-testid="terms-language-switch">
                {languages
                  .filter((l) => l !== data.language)
                  .map((l) => (
                    <button key={l} type="button" onClick={() => setLang(l)} className="mr-3 underline">
                      {t('languageOf', { language: LOCALE_LABELS[l] ?? l })}
                    </button>
                  ))}
              </p>
            )}
            <article className="prose prose-sm max-w-none text-sm leading-relaxed [&_h1]:text-xl [&_h1]:font-semibold [&_h2]:mt-6 [&_h2]:text-base [&_h2]:font-semibold [&_h3]:mt-4 [&_h3]:font-semibold [&_p]:mt-2 [&_ul]:mt-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:mt-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_blockquote]:border-l-2 [&_blockquote]:border-gray-300 [&_blockquote]:pl-3 [&_blockquote]:text-gray-600 [&_table]:mt-2 [&_table]:text-xs [&_th]:text-left [&_th]:pr-3 [&_td]:pr-3" data-testid="terms-body" lang={data.language}>
            <h1>{data.title}</h1>
            <ReactMarkdown>{data.content_md}</ReactMarkdown>
            <p className="mt-8 text-xs text-gray-500">
              {t('footer', { version: data.version, sha: data.content_sha256 })}
            </p>
          </article>
        </>
        )}
        <LegalFooter className="mt-8" />
      </div>
    </div>
  );
}
